import { Button, Icon } from '@toggle/design-system';
import { AssetClassCode, Period, periodSchema } from '@toggle/toggle';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getInsightCondition,
  getInsightTotalTradingDays,
} from '~/api/insights/utils/insight-utils';
import { AssetHeader } from '~/components/asset-header/AssetHeader';
import { ConditionSection } from '~/components/condition-section/ConditionSection';
import { PriceMovement } from '~/components/price-movement/PriceMovement';
import { InsightWidgetData } from '~/stores/use-chat/use-chat.types';
import { useAssetOverviewNavigation } from '~/widgets/asset-overview-widget/hooks/use-asset-overview-navigation/useAssetOverviewNavigation';

import { InsightSummary } from './components/insight-summary/InsightSummary';
import { InsightsReaction } from './components/insights-drawer/components/insights-reaction/InisghtsReaction';
import * as S from './InsightWidget.styles';

export const InsightWidget = ({
  insight,
  predictions,
  entity,
}: InsightWidgetData) => {
  const { navigateToAssetOverview } = useAssetOverviewNavigation(entity);
  const { t } = useTranslation(['scenarioCommander', 'analyze']);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const entityName = entity?.name;
  const isFixedIncome = entity?.asset_class === AssetClassCode.Fi;

  const episodesCount = insight.episodes.length;
  const totalTradingDays = getInsightTotalTradingDays(insight);
  const condition = getInsightCondition(insight, t);

  const tableEpisodes = useMemo(() => {
    if (!predictions.length) {
      return [];
    }

    const tablePredictions = predictions.filter(
      prediction => periodSchema.safeParse(prediction.horizon).success
    );

    return tablePredictions.map(prediction => {
      const { min, max, high, low, median, horizon } = prediction;

      return {
        is_best: horizon == insight.metadata.horizon,
        period: horizon as Period,
        dates: [],
        best: max,
        upside: high,
        median,
        downside: low,
        worst: min,
      };
    });
  }, [predictions]);

  return (
    <S.InsightWidgetRoot data-testid="insight-widget">
      {entity && (
        <S.SplitContainer>
          <AssetHeader entity={entity} />
          <Button
            iconName="Expand"
            label={t('widget:assetOverview.assetOverview')}
            onClick={navigateToAssetOverview}
            variant="tertiary"
            size="small"
          />
        </S.SplitContainer>
      )}

      <InsightSummary insight={insight} isFixedIncome={isFixedIncome} />
      <ConditionSection
        condition={condition}
        episodesCount={episodesCount}
        totalTradingDays={totalTradingDays}
      />
      <InsightsReaction
        entityName={entityName ?? insight.metadata.entity}
        episodeCount={episodesCount}
        isFixedIncome={isFixedIncome}
      />
      <PriceMovement
        prediction={predictions}
        bestHorizon={insight.metadata.horizon}
        horizonToIndexOffset={1}
        episodes={tableEpisodes}
        isFixedIncome={isFixedIncome}
        useDynamicDaysLabel
      />
      <S.DisclaimerWrapper>
        <S.DisclaimerButton onClick={() => setShowDisclaimer(!showDisclaimer)}>
          {t('scenarioCommander:disclaimer')}
          <Icon
            size={12}
            iconName={showDisclaimer ? 'ChevronLightUp' : 'ChevronLightRight'}
            fillColor="var(--icon-soft)"
          />
        </S.DisclaimerButton>
        {showDisclaimer && insight.disclaimer}
      </S.DisclaimerWrapper>
    </S.InsightWidgetRoot>
  );
};
